export type LayerKind =
  | 'overlay'
  | 'terrain-service'
  | 'nwm'
  | 'om'
  | 'emergency'
  | 'wildlife'
  | 'cellular'
  | 'subgroup'
  | 'lightning'

export type BrowseGroup =
  | 'terrain'
  | 'weather'
  | 'imagery'
  | 'hazards'
  | 'land'
  | 'reference'
  | 'emergency'
  | 'wildlife'
  | 'cellular'
  | 'comms'

export interface LayerTierEntry {
  /**
   * Global render/stacking order across ALL layers — lower draws below higher. This is a
   * single total ordering (no tiers): defaults seed terrain low, data mid, weather high,
   * reference on top, but any layer can be freely reordered to any position by the user.
   */
  order: number
  name: string
  kind: LayerKind
  tags: string[]
  browseGroup: BrowseGroup
  children?: string[]
  /**
   * Most-specific region this layer actually has useful data for, shown as a chip under
   * the title. Omitted = worldwide (the unmarked default). Populated from LAYER_REGION below.
   */
  region?: string
  /**
   * Whether the data is model-predicted or measured, shown as a chip under the title.
   * Only set where the distinction is meaningful (weather/observational layers); omitted
   * for static/reference layers. Populated from LAYER_TIMELINESS below.
   */
  timeliness?: 'forecast' | 'observed'
}

const entries: [string, LayerTierEntry][] = [
  // ── Tier 0: Imagery ──────────────────────────────────────────────
  // Aerial orthoimagery (aircraft-flown, pixel-accurate). Static "latest mosaic" sources —
  // not time-aware (they refresh on multi-year cycles, not a daily timeline).
  [
    'usgs-naip',
    {
      order: 6,
      name: 'USGS NAIP (0.3 m Aerial)',
      kind: 'overlay',
      tags: ['naip', 'usda', 'aerial', 'ortho', 'orthoimagery', 'imagery'],
      browseGroup: 'imagery',
    },
  ],
  [
    'swisstopo-swissimage',
    {
      order: 7,
      name: 'SWISSIMAGE (Switzerland 10 cm)',
      kind: 'overlay',
      tags: ['swisstopo', 'swissimage', 'switzerland', 'aerial', 'ortho', 'imagery'],
      browseGroup: 'imagery',
    },
  ],
  [
    'viirs-snpp-truecolor',
    {
      order: 10,
      name: 'VIIRS SNPP True Color',
      kind: 'overlay',
      tags: ['viirs', 'snpp', 'true', 'color', 'imagery', 'satellite'],
      browseGroup: 'imagery',
    },
  ],
  [
    'viirs-noaa20-truecolor',
    {
      order: 20,
      name: 'VIIRS NOAA-20 True Color',
      kind: 'overlay',
      tags: ['viirs', 'noaa', 'true', 'color', 'imagery', 'satellite'],
      browseGroup: 'imagery',
    },
  ],
  [
    'viirs-noaa21-truecolor',
    {
      order: 30,
      name: 'VIIRS NOAA-21 True Color',
      kind: 'overlay',
      tags: ['viirs', 'noaa', 'true', 'color', 'imagery', 'satellite'],
      browseGroup: 'imagery',
    },
  ],
  [
    'goes-east-truecolor',
    {
      order: 40,
      name: 'GOES-East GeoColor',
      kind: 'overlay',
      tags: ['goes', 'east', 'geocolor', 'geostationary', 'imagery'],
      browseGroup: 'imagery',
    },
  ],
  [
    'goes-west-truecolor',
    {
      order: 50,
      name: 'GOES-West GeoColor',
      kind: 'overlay',
      tags: ['goes', 'west', 'geocolor', 'geostationary', 'imagery'],
      browseGroup: 'imagery',
    },
  ],
  [
    'hls-sentinel-truecolor',
    {
      order: 60,
      name: 'Sentinel-2 True Color (HLS)',
      kind: 'overlay',
      tags: ['sentinel', 'hls', 'true', 'color', 'high', 'resolution'],
      browseGroup: 'imagery',
    },
  ],
  [
    'hls-landsat-truecolor',
    {
      order: 70,
      name: 'Landsat True Color (HLS)',
      kind: 'overlay',
      tags: ['landsat', 'hls', 'true', 'color', 'high', 'resolution'],
      browseGroup: 'imagery',
    },
  ],

  // ── Tier 1: Ground-Fixed ─────────────────────────────────────────
  [
    'hillshade',
    {
      order: 80,
      name: 'Hillshade',
      kind: 'terrain-service',
      tags: ['hillshade', 'shaded', 'relief', 'terrain', 'elevation', 'dem'],
      browseGroup: 'terrain',
    },
  ],
  [
    'slope-angle',
    {
      order: 90,
      name: 'Slope Angle Shading',
      kind: 'terrain-service',
      tags: ['slope', 'angle', 'steepness', 'avalanche'],
      browseGroup: 'terrain',
    },
  ],
  [
    'sun-exposure',
    {
      order: 100,
      name: 'Sun Exposure',
      kind: 'terrain-service',
      tags: ['sun', 'exposure', 'aspect', 'solar', 'shade'],
      browseGroup: 'terrain',
    },
  ],
  [
    'land-cover',
    {
      order: 110,
      name: 'Land Cover',
      kind: 'terrain-service',
      tags: ['land', 'cover', 'vegetation', 'forest', 'urban'],
      browseGroup: 'terrain',
    },
  ],
  [
    'contours',
    {
      order: 120,
      name: 'Contour Lines',
      kind: 'terrain-service',
      tags: ['contour', 'elevation', 'topography'],
      browseGroup: 'terrain',
    },
  ],
  [
    'snow-depth',
    {
      order: 130,
      name: 'Snow Depth — Observed (SNODAS)',
      kind: 'terrain-service',
      tags: ['snow', 'depth', 'snodas', 'snowpack', 'observed'],
      browseGroup: 'hazards',
    },
  ],
  [
    'nwm-snowh',
    {
      order: 150,
      name: 'Snow Depth — Modeled (NWM)',
      kind: 'nwm',
      tags: ['snow', 'depth', 'snowpack', 'modeled', 'nwm'],
      browseGroup: 'hazards',
    },
  ],
  [
    'nwm-qsnow',
    {
      order: 160,
      name: 'Snowfall Rate',
      kind: 'nwm',
      tags: ['snow', 'snowfall', 'rate', 'nwm', 'weather'],
      browseGroup: 'hazards',
    },
  ],
  [
    'avalanche-zones',
    {
      order: 170,
      name: 'Avalanche Zones',
      kind: 'terrain-service',
      tags: ['avalanche', 'avy', 'danger', 'forecast', 'snow', 'backcountry'],
      browseGroup: 'hazards',
    },
  ],
  [
    'viirs-snpp-snow-wms',
    {
      order: 180,
      name: 'Snow Coverage — SNPP',
      kind: 'overlay',
      tags: ['snow', 'cover', 'coverage', 'ndsi', 'viirs', 'snpp', 'satellite'],
      browseGroup: 'hazards',
    },
  ],
  [
    'viirs-noaa20-snow-wms',
    {
      order: 200,
      name: 'Snow Coverage — NOAA-20',
      kind: 'overlay',
      tags: ['snow', 'cover', 'coverage', 'ndsi', 'viirs', 'noaa', 'satellite'],
      browseGroup: 'hazards',
    },
  ],
  [
    'vegetation-disturbance',
    {
      order: 210,
      name: 'Vegetation Disturbance Alerts',
      kind: 'overlay',
      tags: ['vegetation', 'disturbance', 'burn', 'scar', 'landslide', 'blowdown'],
      browseGroup: 'hazards',
    },
  ],

  // ── Tier 2: Data ─────────────────────────────────────────────────
  [
    'blm-sma',
    {
      order: 230,
      name: 'Land Management (BLM SMA)',
      kind: 'terrain-service',
      tags: ['blm', 'land', 'management', 'surface', 'agency', 'ownership', 'public'],
      browseGroup: 'land',
    },
  ],
  [
    'padus',
    {
      order: 240,
      name: 'Protected Areas (PAD-US)',
      kind: 'terrain-service',
      tags: [
        'padus',
        'protected',
        'areas',
        'park',
        'wilderness',
        'refuge',
        'access',
        'conservation',
      ],
      browseGroup: 'land',
    },
  ],
  [
    'plss',
    {
      order: 250,
      name: 'PLSS Survey Grid',
      kind: 'terrain-service',
      tags: ['plss', 'survey', 'township', 'range', 'section', 'blm', 'land', 'cadastral'],
      browseGroup: 'land',
    },
  ],
  [
    'backcountry',
    {
      order: 260,
      name: 'Trails & POIs (OSM)',
      kind: 'terrain-service',
      tags: ['backcountry', 'trail', 'peak', 'shelter', 'campsite', 'water', 'hiking', 'path'],
      browseGroup: 'land',
    },
  ],
  [
    'cotrex',
    {
      order: 270,
      name: 'COTREX (Colorado Trails)',
      kind: 'terrain-service',
      tags: ['cotrex', 'colorado', 'trail', 'trailhead', 'hiking', 'cpw'],
      browseGroup: 'land',
    },
  ],
  [
    'cotrex-closures',
    {
      order: 280,
      name: 'Wildlife Closures (COTREX)',
      kind: 'terrain-service',
      tags: ['cotrex', 'colorado', 'closure', 'wildlife', 'seasonal', 'restriction'],
      browseGroup: 'land',
    },
  ],
  [
    'climbing-crags',
    {
      order: 290,
      name: 'Climbs (OpenBeta)',
      kind: 'terrain-service',
      tags: ['climbing', 'crag', 'rock', 'sport', 'trad', 'bouldering', 'openbeta', 'route'],
      browseGroup: 'land',
    },
  ],
  [
    'fire-wildfires',
    {
      order: 300,
      name: 'Active Wildfires (WFIGS)',
      kind: 'terrain-service',
      tags: ['fire', 'wildfire', 'wfigs', 'nifc', 'perimeter', 'incident'],
      browseGroup: 'hazards',
    },
  ],
  [
    'fire-firms',
    {
      order: 320,
      name: 'Fire Detections (FIRMS)',
      kind: 'terrain-service',
      tags: ['fire', 'firms', 'hotspot', 'viirs', 'satellite', 'thermal', 'detection'],
      browseGroup: 'hazards',
    },
  ],
  [
    'fire-restrictions',
    {
      order: 330,
      name: 'Fire Restrictions',
      kind: 'terrain-service',
      tags: ['fire', 'restriction', 'closure', 'nps', 'usfws', 'park', 'refuge', 'ban'],
      browseGroup: 'hazards',
    },
  ],
  [
    'cellular',
    {
      order: 220,
      name: 'US Cellular Coverage',
      kind: 'cellular',
      tags: ['cellular', 'cell', 'coverage', 'carrier', 'att', 'verizon', 'tmobile', 'signal'],
      browseGroup: 'comms',
    },
  ],
  [
    'satellite',
    {
      order: 340,
      name: 'Satellite Positions',
      kind: 'terrain-service',
      tags: [
        'satellite',
        'constellation',
        'iridium',
        'globalstar',
        'starlink',
        'orbit',
        'communication',
      ],
      browseGroup: 'comms',
    },
  ],
  [
    'satellite-los',
    {
      order: 350,
      name: 'Satellite Line of Sight',
      kind: 'terrain-service',
      tags: ['satellite', 'line of sight', 'coverage', 'visibility', 'communication', 'signal'],
      browseGroup: 'comms',
    },
  ],
  [
    'emergency-hospitals',
    {
      order: 360,
      name: 'Hospitals',
      kind: 'emergency',
      tags: ['hospital', 'trauma', 'emergency', 'medical', 'helipad'],
      browseGroup: 'emergency',
    },
  ],
  [
    'emergency-fire-stations',
    {
      order: 370,
      name: 'Fire & EMS Stations',
      kind: 'emergency',
      tags: ['fire', 'ems', 'station', 'emergency'],
      browseGroup: 'emergency',
    },
  ],
  [
    'emergency-law-enforcement',
    {
      order: 380,
      name: 'Law Enforcement',
      kind: 'emergency',
      tags: ['sheriff', 'police', 'law', 'enforcement', 'emergency'],
      browseGroup: 'emergency',
    },
  ],
  [
    'emergency-usfs-offices',
    {
      order: 390,
      name: 'USFS Ranger Stations',
      kind: 'emergency',
      tags: ['usfs', 'ranger', 'forest', 'service', 'station'],
      browseGroup: 'emergency',
    },
  ],
  [
    'wildlife-inat',
    {
      order: 400,
      name: 'Wildlife Sightings',
      kind: 'wildlife',
      tags: ['wildlife', 'inaturalist', 'animal', 'sighting', 'observation'],
      browseGroup: 'wildlife',
    },
  ],
  [
    'wildlife-critical-habitat',
    {
      order: 410,
      name: 'Critical Habitat (USFWS)',
      kind: 'wildlife',
      tags: ['critical', 'habitat', 'endangered', 'threatened', 'species', 'usfws'],
      browseGroup: 'wildlife',
    },
  ],
  [
    'wildlife-nps-alerts',
    {
      order: 420,
      name: 'Park Alerts (NPS)',
      kind: 'wildlife',
      tags: ['nps', 'park', 'alert', 'closure', 'danger', 'caution'],
      browseGroup: 'land',
    },
  ],

  // ── Tier 2: Data — Subgroups (virtual, for browse/search) ────────
  [
    'subgroup-fire',
    {
      order: 310,
      name: 'Fire & Closures',
      kind: 'subgroup',
      tags: ['fire', 'wildfire', 'closure', 'restriction', 'firms', 'wfigs'],
      browseGroup: 'hazards',
      children: ['fire-wildfires', 'fire-firms', 'fire-restrictions', 'om-smoke'],
    },
  ],
  [
    'subgroup-snow-depth',
    {
      order: 140,
      name: 'Snow Depth',
      kind: 'subgroup',
      tags: ['snow', 'depth', 'snowpack', 'snodas', 'nwm', 'observed', 'modeled'],
      browseGroup: 'hazards',
      children: ['snow-depth', 'nwm-snowh'],
    },
  ],
  [
    'subgroup-snow-coverage',
    {
      order: 190,
      name: 'Snow Coverage',
      kind: 'subgroup',
      tags: ['snow', 'cover', 'coverage', 'ndsi', 'viirs', 'snpp', 'noaa', 'satellite'],
      browseGroup: 'hazards',
      children: ['viirs-snpp-snow-wms', 'viirs-noaa20-snow-wms'],
    },
  ],

  // ── Tier 3: Atmospheric ──────────────────────────────────────────
  // NOTE: the NWM Temperature (nwm-t2d), Wind (nwm-wind), Precipitation (nwm-precip),
  // CAPE/Thunderstorm Potential (nwm-cape), Visibility (nwm-vis), Cloud Cover
  // (nwm-tcdc), and Lightning Forecast (nwm-ltng) entries were RETIRED in the weather
  // reorg — each has a worldwide OM equivalent that also covers the US (auto-selecting
  // HRRR there). They are intentionally absent from this registry so stale saved
  // state for them is pruned on load and they can never resurface.
  [
    'nwm-q2d',
    {
      order: 430,
      name: 'Humidity',
      kind: 'nwm',
      tags: ['humidity', 'moisture', 'nwm', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-swdown',
    {
      order: 440,
      name: 'Solar Radiation',
      kind: 'nwm',
      tags: ['solar', 'radiation', 'sun', 'nwm', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'lightning-unified',
    {
      order: 450,
      name: 'Lightning',
      kind: 'lightning',
      tags: ['lightning', 'thunderstorm', 'flash', 'storm', 'worldwide', 'lpi', 'wmo'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-goes_glm',
    {
      order: 460,
      name: 'Lightning (GLM)',
      kind: 'nwm',
      tags: ['lightning', 'observed', 'goes', 'glm', 'flash'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-goes_cloud',
    {
      order: 470,
      name: 'Cloud Cover (GOES)',
      kind: 'nwm',
      tags: ['cloud', 'cover', 'observed', 'goes', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-refc',
    {
      order: 480,
      name: 'Reflectivity',
      kind: 'nwm',
      tags: ['reflectivity', 'radar', 'composite', 'hrrr', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-mrms_radar',
    {
      order: 490,
      name: 'Radar',
      kind: 'nwm',
      tags: ['radar', 'mrms', 'nexrad', 'observed', 'precipitation'],
      browseGroup: 'weather',
    },
  ],

  // ── Tier 3: Atmospheric — Open-Meteo Worldwide ──────────────────
  [
    'subgroup-cloud-forecast',
    {
      order: 500,
      name: 'Cloud Forecast',
      kind: 'subgroup',
      tags: ['cloud', 'cover', 'forecast', 'high', 'mid', 'low', 'open-meteo', 'weather'],
      browseGroup: 'weather',
      children: [
        'om-cloud-cover',
        'om-cloud-cover-high',
        'om-cloud-cover-mid',
        'om-cloud-cover-low',
      ],
    },
  ],
  [
    'om-cloud-cover',
    {
      order: 510,
      name: 'Cloud Cover',
      kind: 'om',
      tags: ['cloud', 'cover', 'worldwide', 'open-meteo', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-cloud-cover-high',
    {
      order: 520,
      name: 'Cloud Cover High',
      kind: 'om',
      tags: ['cloud', 'cover', 'high', 'cirrus', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-cloud-cover-mid',
    {
      order: 530,
      name: 'Cloud Cover Mid',
      kind: 'om',
      tags: ['cloud', 'cover', 'mid', 'alto', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-cloud-cover-low',
    {
      order: 540,
      name: 'Cloud Cover Low',
      kind: 'om',
      tags: ['cloud', 'cover', 'low', 'stratus', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-precipitation',
    {
      order: 550,
      name: 'Precipitation',
      kind: 'om',
      tags: ['precipitation', 'rain', 'snow', 'worldwide', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-cape',
    {
      order: 560,
      name: 'CAPE',
      kind: 'om',
      tags: ['cape', 'convective', 'storm', 'worldwide', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-visibility',
    {
      order: 570,
      name: 'Visibility',
      kind: 'om',
      tags: ['visibility', 'fog', 'haze', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-wind',
    {
      order: 580,
      name: 'Wind',
      kind: 'om',
      tags: ['wind', 'speed', 'gust', 'worldwide', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-temperature',
    {
      order: 590,
      name: 'Temperature',
      kind: 'om',
      tags: ['temperature', 'temp', 'worldwide', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-smoke',
    {
      order: 600,
      name: 'Smoke',
      kind: 'om',
      tags: ['smoke', 'fire', 'air', 'quality', 'open-meteo'],
      browseGroup: 'hazards',
    },
  ],
  [
    'om-freezing-level',
    {
      order: 610,
      name: 'Freezing Level',
      kind: 'om',
      tags: ['freezing', 'level', 'height', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-snow-line',
    {
      order: 620,
      name: 'Snow Line',
      kind: 'om',
      tags: ['snow', 'line', 'snowfall', 'height', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],

  // ── Tier 4: Reference ────────────────────────────────────────────
  [
    'graticule',
    {
      order: 630,
      name: 'Lat/Lon Graticule',
      kind: 'terrain-service',
      tags: ['graticule', 'latitude', 'longitude', 'grid', 'coordinate', 'degree'],
      browseGroup: 'reference',
    },
  ],
  [
    'utm-grid',
    {
      order: 640,
      name: 'UTM Grid',
      kind: 'terrain-service',
      tags: ['utm', 'grid', 'zone', 'universal', 'transverse', 'mercator', 'coordinate'],
      browseGroup: 'reference',
    },
  ],
  [
    'mgrs-grid',
    {
      order: 650,
      name: 'MGRS Grid',
      kind: 'terrain-service',
      tags: ['mgrs', 'military', 'grid', 'reference', 'coordinate', 'nato'],
      browseGroup: 'reference',
    },
  ],
  [
    'declination',
    {
      order: 660,
      name: 'Magnetic Declination',
      kind: 'terrain-service',
      tags: ['declination', 'magnetic', 'north', 'compass', 'isogonic', 'wmm', 'navigation'],
      browseGroup: 'reference',
    },
  ],
  [
    'range-rings',
    {
      order: 670,
      name: 'Range Rings',
      kind: 'terrain-service',
      tags: ['range', 'rings', 'distance', 'radius', 'sar', 'search', 'rescue'],
      browseGroup: 'reference',
    },
  ],
]

// ── Tag metadata ───────────────────────────────────────────────────
// Kept as compact id→value maps (rather than inline on each entry) so the taxonomy is
// reviewable in one place and the entries above stay terse. Merged into the entries below.

// Most-specific region each layer has useful data for. Anything not listed is treated as
// worldwide and shows no region chip (we dropped the "(Worldwide)" name suffix in favour
// of this). Use the tightest accurate scope — e.g. COTREX is Colorado-only, not just US.
const LAYER_REGION: Record<string, string> = {
  // US-scoped (CONUS data products / US agencies)
  'usgs-naip': 'US',
  'swisstopo-swissimage': 'Switzerland',
  'snow-depth': 'US',
  'nwm-snowh': 'US',
  'nwm-qsnow': 'US',
  'avalanche-zones': 'US',
  cellular: 'US',
  'blm-sma': 'US',
  padus: 'US',
  plss: 'US',
  'fire-wildfires': 'US',
  'fire-restrictions': 'US',
  'emergency-hospitals': 'US',
  'emergency-fire-stations': 'US',
  'emergency-law-enforcement': 'US',
  'emergency-usfs-offices': 'US',
  'wildlife-critical-habitat': 'US',
  'wildlife-nps-alerts': 'US',
  'nwm-q2d': 'US',
  'nwm-swdown': 'US',
  'nwm-refc': 'US',
  'nwm-mrms_radar': 'US',
  'nwm-goes_glm': 'US',
  'nwm-goes_cloud': 'US',
  // State-scoped
  cotrex: 'Colorado',
  'cotrex-closures': 'Colorado',
}

// Whether a layer is a model prediction vs a measurement. Only set where the distinction
// is meaningful and non-obvious (weather + observational data); static/reference/terrain
// and plain imagery are left unset.
const LAYER_TIMELINESS: Record<string, 'forecast' | 'observed'> = {
  // Forecast / model output
  'nwm-snowh': 'forecast',
  'nwm-qsnow': 'forecast',
  'nwm-q2d': 'forecast',
  'nwm-swdown': 'forecast',
  'nwm-refc': 'forecast',
  'avalanche-zones': 'forecast',
  'lightning-unified': 'forecast',
  'om-cloud-cover': 'forecast',
  'om-cloud-cover-high': 'forecast',
  'om-cloud-cover-mid': 'forecast',
  'om-cloud-cover-low': 'forecast',
  'om-precipitation': 'forecast',
  'om-cape': 'forecast',
  'om-visibility': 'forecast',
  'om-wind': 'forecast',
  'om-temperature': 'forecast',
  'om-smoke': 'forecast',
  'om-freezing-level': 'forecast',
  'om-snow-line': 'forecast',
  // Observed / measured
  'snow-depth': 'observed',
  'nwm-mrms_radar': 'observed',
  'nwm-goes_glm': 'observed',
  'nwm-goes_cloud': 'observed',
  'viirs-snpp-snow-wms': 'observed',
  'viirs-noaa20-snow-wms': 'observed',
  'fire-firms': 'observed',
  'fire-wildfires': 'observed',
}

for (const [id, entry] of entries) {
  const region = LAYER_REGION[id]
  if (region) entry.region = region
  const timeliness = LAYER_TIMELINESS[id]
  if (timeliness) entry.timeliness = timeliness
}

export const LAYER_TIER_MAP: ReadonlyMap<string, LayerTierEntry> = new Map(entries)

export function getTierEntry(layerId: string): LayerTierEntry | undefined {
  return LAYER_TIER_MAP.get(layerId)
}

export function getDefaultOrder(layerId: string): number {
  return LAYER_TIER_MAP.get(layerId)?.order ?? 1000
}

export function searchLayers(query: string): LayerTierEntry[] {
  if (!query) return []
  const lower = query.toLowerCase()
  const terms = lower.split(/\s+/).filter(t => t.length > 1)
  const results: LayerTierEntry[] = []
  for (const entry of LAYER_TIER_MAP.values()) {
    const nameMatch = terms.every(
      t => entry.name.toLowerCase().includes(t) || entry.tags.some(tag => tag.includes(t))
    )
    if (nameMatch) results.push(entry)
  }
  return results
}
